import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';

const textProps = z.object({
  text: z.string().default(''),
  fontSize: z.number().min(1).default(16),
  fontWeight: z.union([z.string(), z.number()]).optional(),
  fontStyle: z.string().optional(),
  fontFamily: z.string().optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  textColor: z.string().optional(),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  dash: z.array(z.number()).optional(),
  dashEnabled: z.boolean().optional(),
  lineHeight: z.number().min(0.5).max(3).optional(),
  attrs: z.record(z.string(), z.any()).optional(),
});

export type TextShapeProps = z.infer<typeof textProps>;

const DEFAULT_MIN_WIDTH = 20;
const DEFAULT_MIN_HEIGHT = 18;
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_LINE_HEIGHT = 1.2;
const DEFAULT_TEXT_COLOR = '#0F172A';
const DEFAULT_FONT_FAMILY = 'Arial';
const DETERMINISTIC_FALLBACK_FONT_STACK = 'Arial, Inter, sans-serif';

export const CommonTextManifest: ShapeManifest<TextShapeProps> = {
  id: 'text',
  version: '1.0.0',
  category: 'annotation',
  propsSchema: textProps,
  defaultProps: () => ({
    text: 'Text',
    fontSize: DEFAULT_FONT_SIZE,
  }),
  defaultDimensions: { width: 100, height: 30 },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: true,
  },
  renderers: {
    svg: ({ viewBox, viewModel, props }) => {
      const boundsWidth = Math.max(DEFAULT_MIN_WIDTH, Number(viewBox.w) || DEFAULT_MIN_WIDTH);
      const boundsHeight = Math.max(DEFAULT_MIN_HEIGHT, Number(viewBox.h) || DEFAULT_MIN_HEIGHT);
      const style = (viewModel?.style ?? {}) as Record<string, unknown>;
      const attrs = (props.attrs ?? {}) as Record<string, unknown>;

      const fontSize =
        coalesceNumber(props.fontSize, attrs.fontSize, style.fontSize, DEFAULT_FONT_SIZE) ?? DEFAULT_FONT_SIZE;
      const rawFontWeight = coalesce(props.fontWeight, attrs.fontWeight, style.fontWeight);
      const fontWeight = normaliseFontWeight(rawFontWeight);
      const fontStyle = coalesceString(props.fontStyle, attrs.fontStyle, style.fontStyle);
      const fontFamily =
        coalesceString(props.fontFamily, attrs.fontFamily, style.fontFamily) ?? DEFAULT_FONT_FAMILY;
      const textAlign = (coalesceString(props.textAlign, attrs.textAlign, style.textAlign) ??
        'center') as 'left' | 'center' | 'right';
      const verticalAlign = (coalesceString(
        props.verticalAlign,
        attrs.verticalAlign,
        style.verticalAlign,
      ) ?? 'middle') as 'top' | 'middle' | 'bottom';
      const textColor = coalesceString(
        props.textColor,
        attrs.textColor,
        style.textColor,
        DEFAULT_TEXT_COLOR,
      ) ??
        DEFAULT_TEXT_COLOR;
      const fillColor = coalesceString(
        props.fillColor,
        style.fillColor,
        style.fill,
        attrs.fillColor,
        attrs.fill,
      );
      const strokeColor = coalesceString(
        props.strokeColor,
        style.strokeColor,
        style.stroke,
        attrs.strokeColor,
        attrs.stroke,
      );
      const strokeWidth =
        coalesceNumber(props.strokeWidth, style.strokeWidth, attrs.strokeWidth, 0) ?? 0;
      const dashArray = resolveDashArray(props.dash, attrs.dash, style.dash);
      const dashEnabled = resolveDashEnabled(props.dashEnabled, attrs.dashEnabled, style.dashEnabled, dashArray);
      const lineHeightMultiplier =
        coalesceNumber(props.lineHeight, attrs.lineHeight, style.lineHeight, DEFAULT_LINE_HEIGHT) ??
        DEFAULT_LINE_HEIGHT;
      const textDecoration = coalesceString(attrs.textDecoration, style.textDecoration);

      const rawText = coalesceString(props.text, attrs.text) ?? '';
      const normalizedText = normaliseLineEndings(rawText);

      const measure = createTextMeasurer(fontSize, fontWeight, Boolean(fontStyle?.includes('italic')));
      const lines = layoutText(normalizedText, boundsWidth, measure);
      const lineHeight = Math.max(1, fontSize * lineHeightMultiplier);
      const contentHeight = Math.max(lineHeight, lines.length * lineHeight);

      let offsetY = 0;
      if (verticalAlign === 'middle') {
        offsetY = Math.max(0, (boundsHeight - contentHeight) / 2);
      } else if (verticalAlign === 'bottom') {
        offsetY = Math.max(0, boundsHeight - contentHeight);
      }

      const anchorConfig = resolveAnchor(textAlign, boundsWidth);
      const textY = offsetY;
      const textContent = renderLines(lines, anchorConfig.anchorX, lineHeight);

      const rectAttrs: string[] = [
        `x="0"`,
        `y="0"`,
        `width="${formatNumber(boundsWidth)}"`,
        `height="${formatNumber(boundsHeight)}"`,
        `fill="${fillColor ? escapeAttr(fillColor) : 'none'}"`,
        `stroke="${strokeEnabled(strokeColor, strokeWidth) ? escapeAttr(strokeColor!) : 'none'}"`,
        `stroke-width="${formatNumber(strokeEnabled(strokeColor, strokeWidth) ? strokeWidth : 0)}"`,
      ];

      if (dashEnabled && dashArray.length) {
        rectAttrs.push(`stroke-dasharray="${dashArray.map(formatNumber).join(' ')}"`);
      }

      const textAttrs: string[] = [
        `x="${formatNumber(anchorConfig.anchorX)}"`,
        `y="${formatNumber(textY)}"`,
        `fill="${escapeAttr(textColor)}"`,
        `font-size="${formatNumber(fontSize)}"`,
        `font-family="${escapeAttr(toDeterministicFontFamily(fontFamily))}"`,
        `dominant-baseline="hanging"`,
        `text-anchor="${anchorConfig.anchor}"`,
      ];

      if (textDecoration) {
        textAttrs.push(`text-decoration="${escapeAttr(textDecoration)}"`);
      }
      if (fontWeight) {
        textAttrs.push(`font-weight="${escapeAttr(fontWeight)}"`);
      }
      if (fontStyle && fontStyle.includes('italic')) {
        textAttrs.push(`font-style="italic"`);
      }

      const textElement =
        `<text ${textAttrs.join(' ')}>${textContent || '<tspan x="0" dy="0">&#8203;</tspan>'}</text>`;

      return `<g>${renderBackground(rectAttrs)}${textElement}</g>`;
    },
  },
};

function coalesce<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function coalesceString(...values: Array<unknown>): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
}

function coalesceNumber(...values: Array<unknown>): number | undefined {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

function normaliseFontWeight(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const clamped = Math.min(900, Math.max(100, Math.round(value / 100) * 100));
    return String(clamped);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'bold') return '700';
    if (normalized === 'normal') return '400';
    if (/^[1-9]00$/.test(normalized)) return normalized;
    return value;
  }
  return undefined;
}

function resolveDashArray(...values: Array<unknown>): number[] {
  for (const value of values) {
    const array = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[ ,]+/) : null;
    if (!array) continue;
    const parsed = array
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry) && entry > 0);
    if (parsed.length) return parsed;
  }
  return [];
}

function resolveDashEnabled(
  ...values: Array<unknown>
): boolean {
  const dashArray = values[values.length - 1] as number[] | undefined;
  for (let i = 0; i < values.length - 1; i += 1) {
    const value = values[i];
    if (typeof value === 'boolean') return value;
  }
  return Array.isArray(dashArray) && dashArray.length > 0;
}

function strokeEnabled(color: string | undefined, width: number | undefined): boolean {
  if (!color || color === 'transparent') return false;
  if (!Number.isFinite(width)) return false;
  return (width ?? 0) > 0;
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function normaliseLineEndings(value: string): string {
  return normalizeWhitespace(value);
}

function createTextMeasurer(fontSize: number, fontWeight?: string, italic?: boolean): (text: string) => number {
  const baseWidth = fontSize * 0.55;
  const weightFactor = fontWeight && Number(fontWeight) >= 600 ? 1.08 : 1;
  const italicFactor = italic ? 1.03 : 1;

  return (text: string) => {
    if (text.length === 0) return 0;

    let width = 0;
    for (const char of text) {
      const charCode = char.codePointAt(0);
      if (!charCode) continue;

      if (char === ' ') {
        width += fontSize * 0.25;
        continue;
      }
      if (char === '\t') {
        width += fontSize * 1.0;
        continue;
      }

      // Wider glyphs (caps, numbers) get additional weight
      if ((charCode >= 65 && charCode <= 90) || (charCode >= 48 && charCode <= 57)) {
        width += baseWidth * 1.1;
      } else if (charCode > 0xffff) {
        width += baseWidth * 1.2;
      } else {
        width += baseWidth;
      }
    }

    return width * weightFactor * italicFactor;
  };
}

function layoutText(text: string, maxWidth: number, measure: (text: string) => number): string[] {
  const paragraphs = text.split('\n');
  const lines: string[] = [];

  paragraphs.forEach((para, index) => {
    const wrapped = wrapParagraph(para, maxWidth, measure);
    if (!wrapped.length) {
      lines.push('');
    } else {
      lines.push(...wrapped);
    }
    if (index < paragraphs.length - 1 && para.length === 0) {
      lines.push('');
    }
  });

  return lines.length ? lines : [''];
}

function wrapParagraph(paragraph: string, maxWidth: number, measure: (text: string) => number): string[] {
  const tokens = paragraph.split(/(\s+)/);
  const lines: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current.length > 0) {
      lines.push(current);
      current = '';
    }
  };

  for (const token of tokens) {
    if (!token) continue;

    const next = current + token;
    if (measure(next) <= maxWidth || current === '') {
      current = next;
      continue;
    }

    pushCurrent();
    current = token.trimStart();
    if (current && measure(current) > maxWidth) {
      const chars = [...current];
      let buffer = '';
      chars.forEach((ch) => {
        const tentative = buffer + ch;
        if (measure(tentative) <= maxWidth || buffer.length === 0) {
          buffer = tentative;
        } else {
          lines.push(buffer);
          buffer = ch;
        }
      });
      current = buffer;
    }
  }

  if (current.length > 0) {
    lines.push(current);
  }

  return lines;
}

function renderLines(lines: string[], anchorX: number, lineHeight: number): string {
  if (!lines.length) return '';
  return lines
    .map((line, index) => {
      const safeLine = line.length ? escapeText(line) : '&#8203;';
      if (index === 0) {
        return `<tspan x="${formatNumber(anchorX)}" dy="0">${safeLine}</tspan>`;
      }
      return `<tspan x="${formatNumber(anchorX)}" dy="${formatNumber(lineHeight)}">${safeLine}</tspan>`;
    })
    .join('');
}

function renderBackground(rectAttrs: string[]): string {
  return `<rect ${rectAttrs.join(' ')} />`;
}

function resolveAnchor(alignment: 'left' | 'center' | 'right', width: number): { anchor: string; anchorX: number } {
  if (alignment === 'center') {
    return { anchor: 'middle', anchorX: width / 2 };
  }
  if (alignment === 'right') {
    return { anchor: 'end', anchorX: width };
  }
  return { anchor: 'start', anchorX: 0 };
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&apos;');
}

function escapeText(value: string): string {
  return escapeAttr(value);
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function toDeterministicFontFamily(fontFamily: string): string {
  const normalized = fontFamily
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  if (normalized.length === 0) {
    return DETERMINISTIC_FALLBACK_FONT_STACK;
  }
  const lower = new Set(normalized.map((entry) => entry.replace(/^['"]|['"]$/g, '').toLowerCase()));
  if (!lower.has('inter')) normalized.push('Inter');
  if (!lower.has('sans-serif')) normalized.push('sans-serif');
  return normalized.join(', ');
}
